import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { PageHeader } from '@/components/ModernUI';
import { useApiData, useApiMutation } from '@/hooks';
import { 
  Database, RefreshCw, CheckCircle2, AlertTriangle, XCircle,
  Activity, Users, DollarSign, Shield, Heart, TrendingUp,
  Clock, FileText, ArrowRight, Loader2, Wrench, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOMAIN_ICONS = {
  census: Activity,
  clinical: Heart,
  compliance: Shield,
  financial: DollarSign,
  workforce: Users
};

const DOMAIN_COLORS = {
  census: 'bg-blue-500',
  clinical: 'bg-rose-500',
  compliance: 'bg-violet-500',
  financial: 'bg-emerald-500',
  workforce: 'bg-amber-500'
};

const STATUS_COLORS = {
  excellent: 'text-emerald-500',
  good: 'text-blue-500',
  warning: 'text-amber-500',
  critical: 'text-red-500'
};

const STATUS_BG = {
  excellent: 'bg-emerald-50 border-emerald-200',
  good: 'bg-blue-50 border-blue-200',
  warning: 'bg-amber-50 border-amber-200',
  critical: 'bg-red-50 border-red-200'
};

const PRIORITY_COLORS = {
  CRITICAL: 'bg-red-100 text-red-700 border-red-300',
  HIGH: 'bg-orange-100 text-orange-700 border-orange-300',
  MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  LOW: 'bg-blue-100 text-blue-700 border-blue-300'
};

export default function DataHealthDashboard() {
  // Use custom hooks for data fetching
  const { 
    data: healthData, 
    loading, 
    refetch: fetchHealthData, 
    lastUpdated: lastRefresh 
  } = useApiData('/api/data-sync/health-dashboard', {
    showToast: true
  });

  // Data Consistency Score
  const { 
    data: consistencyData, 
    loading: loadingConsistency, 
    refetch: fetchConsistency 
  } = useApiData('/api/data-sync/consistency-score', {
    showToast: false
  });

  const { mutate: syncData, loading: syncing } = useApiMutation('/api/data-sync/sync-all', {
    successMessage: 'Data synchronized successfully',
    onSuccess: () => {
      fetchHealthData();
      fetchConsistency();
    }
  });

  const { mutate: fixData, loading: fixing } = useApiMutation('/api/data-sync/fix-all-inconsistencies', {
    successMessage: 'Data fixed and synced successfully',
    onSuccess: () => {
      setShowFixDialog(false);
      fetchHealthData();
      fetchConsistency();
    }
  });

  // Auto-fix all data issues
  const { mutate: autoFixAll, loading: autoFixing } = useApiMutation('/api/data-sync/fix-all-issues', {
    successMessage: 'All data issues fixed automatically!',
    onSuccess: () => {
      fetchHealthData();
      fetchConsistency();
    }
  });

  const [showFixDialog, setShowFixDialog] = useState(false);
  const [fixValues, setFixValues] = useState({
    current_census: 197,
    monthly_revenue: 485000,
    weekly_admissions: 18,
    weekly_discharges: 15
  });

  const handleSyncData = () => syncData();
  
  const handleFixAllData = () => fixData(fixValues);

  const { mutate: quickFixCensus, loading: quickFixing } = useApiMutation('/api/data-sync/fix-census', {
    successMessage: 'Census fixed successfully',
    onSuccess: fetchHealthData
  });

  const handleQuickFixCensus = (censusValue) => {
    quickFixCensus({ current_census: censusValue, admissions: 18, discharges: 15 });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'excellent': return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
      case 'good': return <CheckCircle2 className="h-5 w-5 text-blue-500" />;
      case 'warning': return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case 'critical': return <XCircle className="h-5 w-5 text-red-500" />;
      default: return null;
    }
  };

  const getHealthColor = (score) => {
    if (score >= 90) return 'bg-emerald-500';
    if (score >= 70) return 'bg-blue-500';
    if (score >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="data-health-loading">
        <PageHeader
          icon={Database}
          title="Data Health Dashboard"
          subtitle="Loading data health status..."
          gradient="from-[#00b0ab] via-[#009590] to-[#48494b]"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-health-dashboard">
      {/* Futuristic Neon Header */}
      <div className="relative overflow-hidden rounded-3xl p-8 text-white">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 animate-gradient-x" />
        
        {/* Mesh overlay */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
                            radial-gradient(circle at 75% 75%, white 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
        
        {/* Glow effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/15 rounded-full blur-3xl" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <h1 className="text-4xl font-black tracking-tight flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm shadow-lg" style={{ boxShadow: '0 0 30px rgba(255,255,255,0.3)' }}>
                <Database className="h-8 w-8" style={{ filter: 'drop-shadow(0 0 10px white)' }} />
              </div>
              <span className="drop-shadow-lg" style={{ textShadow: '0 0 40px rgba(255,255,255,0.5)' }}>
                Data Health Dashboard
              </span>
            </h1>
            <p className="text-white/80 mt-3 text-lg font-medium max-w-xl">
              Monitor data consistency and freshness across all collections
            </p>
          </div>
        </div>
      </div>

      {/* Overall Health Score - Futuristic Card */}
      <Card 
        className="bg-slate-900/80 backdrop-blur-xl border-2 transition-all"
        style={{ 
          borderColor: healthData?.overall_status === 'excellent' ? '#34d39980' : 
                      healthData?.overall_status === 'good' ? '#3b82f680' :
                      healthData?.overall_status === 'warning' ? '#fbbf2480' : '#fb718580',
          boxShadow: `0 0 30px ${healthData?.overall_status === 'excellent' ? 'rgba(52, 211, 153, 0.2)' :
                               healthData?.overall_status === 'good' ? 'rgba(59, 130, 246, 0.2)' :
                               healthData?.overall_status === 'warning' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 113, 133, 0.2)'}`
        }}
      >
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div 
                className="w-24 h-24 rounded-full flex items-center justify-center"
                style={{ 
                  background: getHealthColor(healthData?.overall_health_score || 0).replace('bg-', ''),
                  boxShadow: `0 0 30px ${healthData?.overall_health_score >= 80 ? 'rgba(52, 211, 153, 0.5)' :
                                        healthData?.overall_health_score >= 60 ? 'rgba(59, 130, 246, 0.5)' :
                                        healthData?.overall_health_score >= 40 ? 'rgba(251, 191, 36, 0.5)' : 'rgba(251, 113, 133, 0.5)'}`
                }}
              >
                <span className="text-3xl font-black text-white">{healthData?.overall_health_score || 0}</span>
              </div>
              <div>
                <h2 className="text-2xl font-black capitalize flex items-center gap-2 text-white">
                  {getStatusIcon(healthData?.overall_status)}
                  {healthData?.overall_status || 'Unknown'} Health
                </h2>
                <p className="text-slate-400 font-medium">{healthData?.status_message}</p>
                {lastRefresh && (
                  <p className="text-xs text-slate-500 mt-1 font-medium">
                    <Clock className="h-3 w-3 inline mr-1" />
                    Last updated: {lastRefresh.toLocaleTimeString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={fetchHealthData} 
                disabled={loading}
                className="border-slate-600 text-slate-300 hover:bg-slate-800 font-bold"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                onClick={handleSyncData} 
                disabled={syncing}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 font-bold border-0"
              >
                {syncing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <TrendingUp className="h-4 w-4 mr-2" />
                )}
                Sync Data
              </Button>
              
              {/* Fix Data Dialog */}
              <Dialog open={showFixDialog} onOpenChange={setShowFixDialog}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-400 hover:to-red-500 border-0 font-bold">
                    <Wrench className="h-4 w-4" />
                    Fix Data
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md bg-slate-900 border-slate-700">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-white font-bold">
                      <Settings className="h-5 w-5 text-cyan-400" />
                      Fix Data Inconsistencies
                    </DialogTitle>
                    <DialogDescription className="text-slate-400">
                      Enter the correct current values. This will update all related collections to ensure consistency.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="census" className="text-slate-300 font-bold">Current Census (ADC)</Label>
                      <Input
                        id="census"
                        type="number"
                        value={fixValues.current_census}
                        onChange={(e) => setFixValues({...fixValues, current_census: parseInt(e.target.value) || 0})}
                        placeholder="e.g., 197"
                        className="bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="revenue" className="text-slate-300 font-bold">Monthly Revenue ($)</Label>
                      <Input
                        id="revenue"
                        type="number"
                        value={fixValues.monthly_revenue}
                        onChange={(e) => setFixValues({...fixValues, monthly_revenue: parseInt(e.target.value) || 0})}
                        placeholder="e.g., 485000"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="admissions">Weekly Admissions</Label>
                        <Input
                          id="admissions"
                          type="number"
                          value={fixValues.weekly_admissions}
                          onChange={(e) => setFixValues({...fixValues, weekly_admissions: parseInt(e.target.value) || 0})}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="discharges">Weekly Exits (Total)</Label>
                        <Input
                          id="discharges"
                          type="number"
                          value={fixValues.weekly_discharges}
                          onChange={(e) => setFixValues({...fixValues, weekly_discharges: parseInt(e.target.value) || 0})}
                        />
                      </div>
                    </div>
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        This will create 12 weeks of census history and 12 months of financial data based on these values.
                      </AlertDescription>
                    </Alert>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowFixDialog(false)}>Cancel</Button>
                    <Button onClick={handleFixAllData} disabled={fixing}>
                      {fixing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wrench className="h-4 w-4 mr-2" />}
                      Fix All Data
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Quick Fix Buttons */}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground mr-2">Quick Fix Census:</span>
            {[178, 185, 190, 195, 197, 200].map((val) => (
              <Button 
                key={val} 
                variant="outline" 
                size="sm" 
                onClick={() => handleQuickFixCensus(val)}
                disabled={fixing}
                className="text-xs"
              >
                {val}
              </Button>
            ))}
          </div>

          {/* Data Consistency Status */}
          <div className="mt-4 flex items-center gap-2">
            {healthData?.data_consistent ? (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Data Consistent
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
                <AlertTriangle className="h-3 w-3 mr-1" />
                {healthData?.consistency_issues?.length || 0} Consistency Issues
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Automated Data Consistency Score Card */}
      <Card 
        className="bg-slate-900/80 backdrop-blur-xl border-2 transition-all"
        style={{ 
          borderColor: consistencyData?.score >= 95 ? '#34d39980' : 
                      consistencyData?.score >= 80 ? '#3b82f680' :
                      consistencyData?.score >= 60 ? '#fbbf2480' : '#fb718580',
          boxShadow: `0 0 30px ${consistencyData?.score >= 95 ? 'rgba(52, 211, 153, 0.2)' :
                               consistencyData?.score >= 80 ? 'rgba(59, 130, 246, 0.2)' :
                               consistencyData?.score >= 60 ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 113, 133, 0.2)'}`
        }}
        data-testid="consistency-score-card"
      >
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-white font-bold">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-cyan-400" />
              Automated Data Consistency
            </div>
            <Button 
              onClick={() => autoFixAll()} 
              disabled={autoFixing}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 font-bold border-0"
              data-testid="auto-fix-all-btn"
            >
              {autoFixing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Wrench className="h-4 w-4 mr-2" />
              )}
              Auto-Fix All Issues
            </Button>
          </CardTitle>
          <CardDescription className="text-slate-400">
            Automatically detect and fix data inconsistencies across all collections
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingConsistency ? (
            <Skeleton className="h-32" />
          ) : (
            <div className="space-y-4">
              {/* Score Display */}
              <div className="flex items-center gap-6">
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center"
                  style={{ 
                    background: consistencyData?.score >= 95 ? 'linear-gradient(135deg, #10b981, #059669)' :
                               consistencyData?.score >= 80 ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' :
                               consistencyData?.score >= 60 ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
                    boxShadow: `0 0 30px ${consistencyData?.score >= 95 ? 'rgba(16, 185, 129, 0.5)' :
                                          consistencyData?.score >= 80 ? 'rgba(59, 130, 246, 0.5)' :
                                          consistencyData?.score >= 60 ? 'rgba(245, 158, 11, 0.5)' : 'rgba(239, 68, 68, 0.5)'}`
                  }}
                >
                  <span className="text-2xl font-black text-white">{consistencyData?.score || 0}%</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white capitalize">{consistencyData?.status || 'Unknown'}</h3>
                  <p className="text-slate-400 text-sm">
                    {consistencyData?.checks_passed || 0} / {consistencyData?.total_checks || 0} checks passed
                  </p>
                  {consistencyData?.last_checked && (
                    <p className="text-xs text-slate-500 mt-1">
                      <Clock className="h-3 w-3 inline mr-1" />
                      Last checked: {new Date(consistencyData.last_checked).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>

              {/* Issues List */}
              {consistencyData?.issues?.length > 0 ? (
                <div className="space-y-2">
                  <h4 className="text-sm font-bold text-slate-300">Issues Found ({consistencyData.issue_count})</h4>
                  {consistencyData.issues.map((issue, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg border ${
                        issue.severity === 'high' ? 'bg-red-500/10 border-red-500/30' :
                        issue.severity === 'medium' ? 'bg-amber-500/10 border-amber-500/30' :
                        'bg-blue-500/10 border-blue-500/30'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {issue.severity === 'high' ? (
                            <XCircle className="h-4 w-4 text-red-400" />
                          ) : issue.severity === 'medium' ? (
                            <AlertTriangle className="h-4 w-4 text-amber-400" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-blue-400" />
                          )}
                          <span className="font-bold text-white text-sm">{issue.type.replace(/_/g, ' ')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge 
                            className={`${
                              issue.severity === 'high' ? 'bg-red-500/20 text-red-400' :
                              issue.severity === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}
                          >
                            {issue.severity}
                          </Badge>
                          {issue.fixable && (
                            <Badge className="bg-emerald-500/20 text-emerald-400">Auto-fixable</Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-slate-400 text-sm mt-1">{issue.message}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert className="bg-emerald-500/10 border-emerald-500/30">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  <AlertTitle className="text-emerald-400 font-bold">All Checks Passed!</AlertTitle>
                  <AlertDescription className="text-emerald-300">
                    Your data is 100% consistent across all collections. No issues detected.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Domain Health Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {healthData?.domains && Object.entries(healthData.domains).map(([domain, data]) => {
          const Icon = DOMAIN_ICONS[domain] || Database;
          return (
            <Card key={domain} className="relative overflow-hidden" data-testid={`domain-${domain}`}>
              <div className={`absolute top-0 left-0 w-full h-1 ${DOMAIN_COLORS[domain]}`} />
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-5 w-5 ${STATUS_COLORS[data.status]}`} />
                    <CardTitle className="text-sm capitalize">{domain}</CardTitle>
                  </div>
                  {getStatusIcon(data.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Health Score</span>
                      <span className="font-bold">{data.health_score}%</span>
                    </div>
                    <Progress value={data.health_score} className="h-2" />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Records</span>
                      <p className="font-medium">{data.record_count}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Complete</span>
                      <p className="font-medium">{data.completeness ?? data.health_score ?? 100}%</p>
                    </div>
                  </div>

                  {data.days_since_update !== null && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {data.days_since_update === 0 ? 'Updated today' : 
                       data.days_since_update === 1 ? 'Updated yesterday' :
                       `Updated ${data.days_since_update} days ago`}
                    </div>
                  )}

                  {/* Key Metrics */}
                  <div className="pt-2 border-t">
                    <p className="text-xs text-muted-foreground mb-1">Key Metrics</p>
                    <div className="space-y-1">
                      {Object.entries(data.key_metrics || {}).slice(0, 3).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-xs">
                          <span className="text-muted-foreground truncate">{key}</span>
                          <span className="font-medium">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="text-[10px] text-muted-foreground">
                    Source: {data.primary_source}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recommendations */}
      {healthData?.recommendations?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Recommendations
            </CardTitle>
            <CardDescription>
              Actions to improve data health
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {healthData.recommendations.map((rec, idx) => (
                <Alert key={idx} className={`border ${PRIORITY_COLORS[rec.priority]}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <AlertTitle className="flex items-center gap-2">
                        <Badge variant="outline" className={PRIORITY_COLORS[rec.priority]}>
                          {rec.priority}
                        </Badge>
                        <span className="font-medium">{rec.domain}</span>
                      </AlertTitle>
                      <AlertDescription className="mt-1">
                        <p>{rec.message}</p>
                        <p className="mt-1 flex items-center text-sm font-medium">
                          <ArrowRight className="h-3 w-3 mr-1" />
                          {rec.action}
                        </p>
                      </AlertDescription>
                    </div>
                  </div>
                </Alert>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Consistency Issues */}
      {healthData?.consistency_issues?.length > 0 && (
        <Card className="border-red-200 bg-red-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Data Consistency Issues
            </CardTitle>
            <CardDescription>
              These metrics have different values across collections
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {healthData.consistency_issues.map((issue, idx) => (
                <div key={idx} className="p-3 bg-white rounded-lg border border-red-200">
                  <p className="font-medium">{issue.metric}</p>
                  <div className="mt-1 text-sm text-muted-foreground">
                    <span>census_records: <strong>{issue.census_records}</strong></span>
                    <span className="mx-2">vs</span>
                    <span>program_overview: <strong>{issue.program_overview}</strong></span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{issue.recommendation}</p>
                </div>
              ))}
              <Button variant="outline" className="mt-2" onClick={handleSyncData} disabled={syncing}>
                {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Fix Consistency Issues
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Issues Message */}
      {healthData?.recommendations?.length === 0 && healthData?.data_consistent && (
        <Alert className="bg-emerald-50 border-emerald-200">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <AlertTitle className="text-emerald-700">All Systems Healthy</AlertTitle>
          <AlertDescription className="text-emerald-600">
            All data sources are healthy, consistent, and up to date.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
